import { useOrganization } from 'context/OrganizationContext';
import { BreedingPlan, BreedingplanService, Contact, Horse, MareCycleCheck } from 'openapi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { OptionItemInterface } from 'ui/Inputs/SelectInput';
import type { ExtendedMareCycleCheck } from '../index';
import { ApiPromises } from 'utilities/ApiPromises';
import ApiErrorParser, { ApiErrorType } from 'api/ApiErrorParser';
import { useAccount } from 'context/AccountContext';

interface ReturnType {
  breedingPlanOptions: OptionItemInterface[];
  selectedBreedingPlan: BreedingPlan | undefined;
}

interface Props {
  setValue: UseFormSetValue<ExtendedMareCycleCheck>;
  watch: UseFormWatch<ExtendedMareCycleCheck>;
  selectedMareUid: string | undefined;
  selectedMare: Horse | undefined;
  modalIsOpen: boolean;
  existingMareCycleCheck: MareCycleCheck | undefined;
  contacts: Contact[] | undefined;
}

/**
 * Small hook to handle the breeding plan selection for the semen order
 */
function useMareCycleBreedingPlan({
  setValue,
  watch,
  selectedMareUid,
  selectedMare,
  modalIsOpen,
  existingMareCycleCheck,
  contacts,
}: Props): ReturnType {
  const [breedingPlans, setBreedingPlans] = useState<BreedingPlan[]>();
  const [selectedBreedingPlan, setSelectedBreedingPlan] = useState<BreedingPlan>();

  const { selectedOrganizationUid, generateCacheKey } = useOrganization();
  const { accountDetails } = useAccount();

  const breedingPlanUidFormValue = watch('breeding_plan_uid');

  const breedingPlanOptions = useMemo((): OptionItemInterface[] => {
    return (breedingPlans ?? []).map(
      plan =>
        ({
          id: plan.uid,
          name: String(plan.number),
        }) as OptionItemInterface,
    );
  }, [breedingPlans]);

  /* Load the Api data. Mainly the contacts if they are not already loaded.
   */
  const loadApiData = useCallback(
    (mareUid: string) => {
      const promises = new ApiPromises();
      if (!selectedOrganizationUid) return promises;

      promises.appendList<BreedingPlan>(
        'contacts',
        () =>
          BreedingplanService.breedingplanList({
            mareOrganisationUid: selectedOrganizationUid,
            mareUid,
          }),
        setBreedingPlans,
        generateCacheKey('breeding-plans'),
      );

      return promises;
    },
    [generateCacheKey, selectedOrganizationUid],
  );

  /**
   * Load the Api data
   */
  useEffect(() => {
    let mareUid = undefined;
    if (selectedMareUid && modalIsOpen) {
      mareUid = selectedMareUid;
    } else if (selectedMare && modalIsOpen) {
      mareUid = selectedMare.uid;
    } else if (existingMareCycleCheck && modalIsOpen) {
      mareUid = existingMareCycleCheck.mare_uid;
    }

    if (mareUid) {
      const promise = loadApiData(mareUid);
      promise.watchAll().catch(e => {
        const error = new ApiErrorParser(e);
        if (error.errorType !== ApiErrorType.CancelError) {
          console.warn('Failed to fetch ApiData for the mareCycleBreedingPlan.', e);
        }
      });

      return () => promise.cancel();
    } else {
      setBreedingPlans(undefined);
    }
  }, [existingMareCycleCheck, loadApiData, modalIsOpen, selectedMare, selectedMareUid]);

  /**
   * Set the values of the breeding plan if we have a selected breeding plan
   * but just for a mare cycle check
   */
  useEffect(() => {
    if (!existingMareCycleCheck && breedingPlanUidFormValue) {
      const selectedPlan = breedingPlans?.find(plan => plan.uid === breedingPlanUidFormValue);

      if (selectedPlan) {
        selectedPlan.stallion_uid && setValue('stallion_uid', selectedPlan.stallion_uid);
        setValue('requester_uid', selectedPlan?.requester_uid ?? undefined);
      }
    } else if (breedingPlans && existingMareCycleCheck) {
      // for an existing mare cycle check we set the requester and stallion from the breeding plan
      const findPlan = breedingPlans.find(plan => plan.uid === existingMareCycleCheck.breeding_plan.uid);
      if (findPlan) {
        findPlan.requester_uid && setValue('requester_uid', findPlan.requester_uid);
        findPlan.stallion_uid && setValue('stallion_uid', findPlan.stallion_uid);
      }
    }
  }, [accountDetails, breedingPlanUidFormValue, breedingPlans, contacts, existingMareCycleCheck, setValue]);

  /**
   * In some situations its impossible by the browser to select the correct breeding plan
   * as we first have the value and after that we receive the options.
   * To bypass this we set the value again if the options are loaded
   */
  useEffect(() => {
    if (breedingPlans) {
      setValue('breeding_plan_uid', breedingPlanUidFormValue);
    }
  }, [breedingPlanOptions]); //eslint-disable-line

  /**
   * In case we just have one plan, we could set the value directly
   */
  useEffect(() => {
    if (breedingPlans && existingMareCycleCheck) {
      const findPlan = breedingPlans.find(plan => plan.uid === existingMareCycleCheck.breeding_plan.uid);
      if (findPlan) {
        setSelectedBreedingPlan(findPlan);
      }
    } else if (breedingPlans && breedingPlans.length === 1) {
      setSelectedBreedingPlan(breedingPlans[0]);
    } else if (breedingPlanUidFormValue) {
      const findPlan = breedingPlans?.find(plan => plan.uid === breedingPlanUidFormValue);
      if (findPlan) {
        setSelectedBreedingPlan(findPlan);
      }
    } else {
      setSelectedBreedingPlan(undefined);
    }
  }, [breedingPlanUidFormValue, breedingPlans, existingMareCycleCheck]);

  /**
   * Update the form value if we have a selected breeding plan
   */
  useEffect(() => {
    setValue('breeding_plan_uid', selectedBreedingPlan?.uid);
  }, [selectedBreedingPlan, setValue]);

  /**
   * Reset the values when we close the modal
   */
  useEffect(() => {
    if (!modalIsOpen) {
      setBreedingPlans(undefined);
      setSelectedBreedingPlan(undefined);
    }
  }, [modalIsOpen]);

  /**
   * For a new mare cycle check we set the requester uid to the preferred stable contact
   * if we have no breedingPlan(s) and that contact is a semen collection station
   */
  useEffect(() => {
    if (selectedMareUid && !existingMareCycleCheck && breedingPlanOptions.length === 0) {
      const preferredStableContact = contacts?.find(contact => contact.stable_location_uid === accountDetails?.preferred_stable);
      if (preferredStableContact && preferredStableContact.is_semen_collection_station) {
        setValue('requester_uid', preferredStableContact.uid);
      }
    }
  }, [accountDetails, breedingPlanOptions.length, contacts, existingMareCycleCheck, selectedMareUid, setValue]);

  return {
    breedingPlanOptions,
    selectedBreedingPlan,
  };
}

export default useMareCycleBreedingPlan;
